import { AnalysisSuggestion, AnalysisSuggestionGame } from '../features/analysis-suggestions/types/AnalysisSuggestion'
import { InternalOrganization, InternalUser } from '../features/organizations/types/Organizations'
import { jsonAnswer, optionalJson } from './helpers'
import { internalAccountApi } from './internal-account'
import { internalApiserverApi } from './internal-apiserver'

export const analysisSuggestionGameFields = [
  'name',
  'artist',
  'gpsPerMarket',
  'icon',
  'icons',
  'stage',
  'internal',
  'appId',
  'stores',
  'markets',
  'lastAnalysisAt',
  'latestAnalysis',
  'sranks',
  'sdranks',
]

/** `confirmed` is false when the POST failed; `stored` then still tells whether the change landed. */
export type AnalysisSuggestionSaved = { stored?: AnalysisSuggestion; confirmed: boolean }

const analysisSuggestionsApi = internalApiserverApi.injectEndpoints({
  endpoints: (builder) => ({
    getAnalysisSuggestions: builder.query<AnalysisSuggestion[], void>({
      query: () => 'i/analysis-requests/list',
    }),
    getAnalysisSuggestionGames: builder.query<AnalysisSuggestionGame[], string[]>({
      query: (gameIds) => ({ url: 'games/list', method: 'POST', body: { gameIds, include: analysisSuggestionGameFields.join(',') } }),
    }),
    // The backend has no delete for suggestions, and the POST replaces the stored suggestion with the body.
    // Nothing is invalidated: the page puts the read-back suggestion into the list with `storeListedAnalysisSuggestion`.
    saveAnalysisSuggestion: builder.mutation<AnalysisSuggestionSaved, AnalysisSuggestion>({
      async queryFn(suggestion, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: 'i/analysis-requests', method: 'POST', body: suggestion, headers: jsonAnswer, responseHandler: 'text' })
        // An unknown id answers 204 with no body.
        const readBack = await baseQuery({ url: `i/analysis-requests/${suggestion.id}`, responseHandler: optionalJson })
        if (readBack.error) return { error: saved.error ?? readBack.error }
        return { data: { stored: (readBack.data as AnalysisSuggestion | null) ?? undefined, confirmed: !saved.error } }
      },
    }),
  }),
})

/** Replaces one suggestion in the cached list with the stored one. */
export const storeListedAnalysisSuggestion = (stored: AnalysisSuggestion) =>
  analysisSuggestionsApi.util.updateQueryData('getAnalysisSuggestions', undefined, (suggestions) => {
    const index = suggestions.findIndex(({ id }) => id === stored.id)
    if (index >= 0) suggestions[index] = stored
  })

const analysisSuggestionAccountApi = internalAccountApi.injectEndpoints({
  endpoints: (builder) => ({
    // Answers only the organizations it finds.
    getInternalOrganizationsByIds: builder.query<InternalOrganization[], string[]>({
      query: (organizationIds) => ({ url: 'orgs/list', method: 'POST', body: { organizationIds } }),
    }),
    // Answers only the users it finds.
    getInternalUsersByUsernames: builder.query<InternalUser[], string[]>({
      query: (usernames) => ({ url: 'users/list/username', method: 'POST', body: { usernames } }),
    }),
  }),
})

export const { useGetAnalysisSuggestionsQuery, useGetAnalysisSuggestionGamesQuery, useSaveAnalysisSuggestionMutation } = analysisSuggestionsApi
export const { useGetInternalOrganizationsByIdsQuery, useGetInternalUsersByUsernamesQuery } = analysisSuggestionAccountApi
